import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { compact, keys, omit } from 'lodash-es';
import { BottomSheetContentLayout, Column, MobileSummaryCard, TextButton, useOverlayContext } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import Filter from '../Filter/Filter';
import { MobileAddFilterList } from '../MobileAddFilterList/MobileAddFilterList';
import { MobileSortPicker } from '../MobileTableFilters/MobileSortPicker';
import { isEmptyFilterValue, makeInitialFilterValue } from '../TableFilters.utils';
import { SearchFilterType } from '../types';
export const GetMobileFilterListTID = (parentTestId)=>getTestIds(parentTestId);
function deriveSortLabel(sort, sortOptions, sortableColumnIndices) {
    if (!sort) return undefined;
    const optionIndex = sortableColumnIndices.indexOf(sort.colIndex);
    return optionIndex >= 0 ? sortOptions[optionIndex].label : undefined;
}
function deriveSelectedSortOptionId(sort, sortOptions, sortableColumnIndices) {
    if (!sort) return undefined;
    const optionIndex = sortableColumnIndices.indexOf(sort.colIndex);
    return optionIndex >= 0 ? sortOptions[optionIndex].id : undefined;
}
function resolveMultiSelectLabels(values, config) {
    if (config.type !== SearchFilterType.MultiSelectFilter || values.length === 0) return undefined;
    const labelByValue = Object.fromEntries(config.options.map((o)=>typeof o === 'string' ? [
            o,
            o
        ] : [
            o.value,
            o.label
        ]));
    return values.map((v)=>{
        var _labelByValue_v;
        return (_labelByValue_v = labelByValue[v]) !== null && _labelByValue_v !== void 0 ? _labelByValue_v : v;
    }).join(', ');
}
export const MobileFilterList = (param)=>{
    let { testId, filterLabels, filterConfig, initialDraftFilters, initialDraftSort, sortOptions, sortableColumnIndices, filterGroups, formatFilterDescription, onApply } = param;
    const { MobileFilterListTID } = useMemo(()=>GetMobileFilterListTID(testId), [
        testId
    ]);
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const [draftFilters, setDraftFilters] = useState(initialDraftFilters);
    const [draftSort, setDraftSort] = useState(initialDraftSort);
    const draftFilterKeys = keys(draftFilters);
    const allFilterKeys = keys(filterConfig);
    const availableFilters = allFilterKeys.filter((f)=>!draftFilterKeys.includes(f));
    const filterDescriptions = draftFilterKeys.reduce((acc, filterKey)=>{
        var _ref;
        const value = draftFilters[filterKey];
        const description = (_ref = formatFilterDescription === null || formatFilterDescription === void 0 ? void 0 : formatFilterDescription(filterKey, value)) !== null && _ref !== void 0 ? _ref : Array.isArray(value) ? resolveMultiSelectLabels(value, filterConfig[filterKey]) : undefined;
        if (description) acc[filterKey] = description;
        return acc;
    }, {});
    const sortLabel = sortOptions ? deriveSortLabel(draftSort, sortOptions, sortableColumnIndices) : undefined;
    function removeFilter(filter) {
        setDraftFilters((prev)=>omit(prev, filter));
    }
    function openFilterSheet(filter, initialValue) {
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(Filter, {
                testIdPrefix: testId !== null && testId !== void 0 ? testId : '',
                label: filterLabels[filter],
                filter: filter,
                filterConfig: filterConfig[filter],
                selectedFilter: initialValue !== null && initialValue !== void 0 ? initialValue : draftFilters[filter],
                onChange: (value)=>{
                    setDraftFilters((prev)=>_object_spread_props(_object_spread({}, prev), {
                            [filter]: value
                        }));
                },
                onRemove: ()=>{}
            })
        });
    }
    function openSortPicker() {
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(MobileSortPicker, {
                testId: testId,
                sortOptions: sortOptions,
                selectedSortOptionId: deriveSelectedSortOptionId(draftSort, sortOptions, sortableColumnIndices),
                onSelect: (option)=>{
                    const optionIndex = sortOptions.indexOf(option);
                    setDraftSort({
                        colIndex: sortableColumnIndices[optionIndex],
                        sort: option.defaultDirection
                    });
                    popBottomSheetPage();
                }
            })
        });
    }
    function handleAddFilterClick() {
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(MobileAddFilterList, {
                filterLabels: filterLabels,
                availableFilters: availableFilters,
                groups: filterGroups,
                onSelect: (filter)=>{
                    const initialValue = makeInitialFilterValue(filterConfig[filter]);
                    setDraftFilters((prev)=>_object_spread_props(_object_spread({}, prev), {
                            [filter]: initialValue
                        }));
                    popBottomSheetPage();
                    openFilterSheet(filter, initialValue);
                }
            })
        });
    }
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentGutter: "m",
        footer: {
            paddingTop: 8,
            primaryAction: {
                text: 'Apply',
                onClick: ()=>onApply(draftFilters, draftSort),
                testId: MobileFilterListTID.ApplyButton()
            }
        },
        children: /*#__PURE__*/ _jsxs(Column, {
            gap: 8,
            children: [
                sortOptions && /*#__PURE__*/ _jsx(MobileSummaryCard, {
                    testId: MobileFilterListTID.SortCard(),
                    items: compact([
                        _object_spread_props(_object_spread({
                            title: 'Sort by'
                        }, sortLabel && {
                            body: sortLabel,
                            bodyTestId: MobileFilterListTID.SortCardBody()
                        }), {
                            actions: [
                                {
                                    type: 'edit',
                                    testId: MobileFilterListTID.SortCardEditButton(),
                                    onClick: openSortPicker
                                }
                            ]
                        })
                    ])
                }),
                allFilterKeys.length > 0 && /*#__PURE__*/ _jsx(MobileSummaryCard, {
                    title: "Filters",
                    testId: MobileFilterListTID.Root(),
                    items: compact([
                        draftFilterKeys.length === 0 && {
                            body: 'No filters applied',
                            bodyTestId: MobileFilterListTID.NoFiltersApplied()
                        },
                        ...draftFilterKeys.map((filterKey)=>{
                            const description = (filterDescriptions === null || filterDescriptions === void 0 ? void 0 : filterDescriptions[filterKey]) || (isEmptyFilterValue(draftFilters[filterKey], filterConfig[filterKey]) ? 'Select all' : undefined);
                            return _object_spread_props(_object_spread({
                                title: filterLabels[filterKey],
                                titleTestId: MobileFilterListTID.FilterRow(String(filterKey))
                            }, description && {
                                body: description,
                                bodyEllipsis: true,
                                bodyTestId: MobileFilterListTID.FilterDescription(String(filterKey))
                            }), {
                                actions: [
                                    {
                                        type: 'edit',
                                        testId: MobileFilterListTID.EditButton(String(filterKey)),
                                        onClick: ()=>openFilterSheet(filterKey)
                                    },
                                    {
                                        type: 'remove',
                                        testId: MobileFilterListTID.RemoveButton(String(filterKey)),
                                        onClick: ()=>removeFilter(filterKey)
                                    }
                                ]
                            });
                        }),
                        availableFilters.length > 0 && {
                            body: /*#__PURE__*/ _jsx(TextButton, {
                                testId: MobileFilterListTID.AddFilterButton(),
                                underline: "none",
                                weight: "normal",
                                prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                                onClick: handleAddFilterClick,
                                children: "Add filter"
                            })
                        }
                    ])
                })
            ]
        })
    });
};
